import { Box, Modal, Text } from '@tocktix/pantry';
import { FunctionComponent, useState } from 'react';
import { useActions } from 'utils/connect';
import { userClient } from 'tools/bff/client';

interface AddSuspectedBotModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const AddSuspectedBotModal: FunctionComponent<AddSuspectedBotModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { actions } = useActions();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await userClient.addSuspectedBot({ gdusUuid: uuid });
      await onSubmit();
      handleClose();
      if (response.created) {
        actions.toast.showInfo('User added to suspected bots');
      } else {
        actions.toast.showInfo('User is already flagged as a suspected bot');
      }
    } catch (reason) {
      setError('Error adding user to suspected bots: ' + reason);
      actions.toast.showError('Error adding user to suspected bots');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      id="add-suspected-bot-modal"
      open={open}
      onClose={handleClose}
      size="md"
      data-testid="add-suspected-bot-modal"
    >
      <Modal.Header title="Add to suspected bots" onClose={handleClose} border />
      <Modal.Body>
        <Box display="flex" flexDirection="column" gap={200}>
          <Text variant="body" color="fgSubtle">
            This flags the user as a suspected bot in Resy. It only records the flag — the account
            is not deactivated and no termination notice is sent. Those remain separate manual
            actions.
          </Text>
          {error && (
            <Text variant="body" color="fgDangerBase">
              {error}
            </Text>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer
        actions={{
          primary: {
            label: isSubmitting ? 'Adding...' : 'Add to suspected bots',
            type: 'button',
            disabled: isSubmitting,
            onClick: handleSubmission,
          },
          cancel: {
            label: 'Cancel',
            onClick: handleClose,
          },
        }}
      />
    </Modal>
  );
};

export default AddSuspectedBotModal;
